import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { 
  ConnectComponentsProvider,
  ConnectAccountOnboarding 
} from '@stripe/react-connect-js';
import api from '../utils/api';
import { toast } from 'sonner';

const StripeConnectOnboardingModal = ({ isOpen, onClose, onComplete }) => {
  const [stripeConnectInstance, setStripeConnectInstance] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      startOnboarding();
    }
  }, [isOpen]);

  const startOnboarding = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get client secret from backend
      const response = await api.post('/organizations/stripe-connect/onboard');
      const { clientSecret: secret } = response.data;

      if (!secret) {
        throw new Error('No client secret received');
      }

      setClientSecret(secret);

      // Initialize Stripe Connect
      const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      
      if (!publishableKey) {
        throw new Error('Stripe publishable key not configured');
      }

      const instance = await loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret: async () => secret,
        appearance: {
          overlays: 'dialog',
          variables: {
            colorPrimary: '#2563EB',
            colorBackground: '#ffffff',
            colorText: '#1a1a1a',
            borderRadius: '12px',
          },
        },
      });

      setStripeConnectInstance(instance);
    } catch (err) {
      console.error('Error starting onboarding:', err);
      setError(err.message || 'Failed to start Stripe onboarding');
      toast.error('Failed to start Stripe setup');
    } finally {
      setLoading(false);
    }
  };

  const handleExit = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm sm:p-4">
      {/* Card on desktop, full-screen on mobile */}
      <div className="relative w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[90vh] bg-white sm:rounded-xl sm:shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Connect Payment Processing</h2>
            <p className="text-sm text-gray-600 mt-1">Complete the setup to start receiving payments</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
              <p className="text-gray-600">Loading Stripe onboarding...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600 font-semibold mb-2">Setup Error</p>
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={startOnboarding}
                className="mt-4 bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {stripeConnectInstance && clientSecret && !loading && !error && (
            <>
              <style>{`
                /* Mobile responsive Stripe iframe */
                iframe[name^="stripe"] {
                  box-shadow: none !important;
                  max-width: 100% !important;
                  width: 100% !important;
                }
                .StripeElement {
                  box-shadow: none !important;
                }
                
                /* Scale down Stripe content on mobile */
                @media (max-width: 640px) {
                  iframe[name^="stripe"] {
                    transform: scale(0.95);
                    transform-origin: top left;
                  }
                }
              `}</style>
              <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
                <div className="min-h-[400px] overflow-x-auto">
                  <ConnectAccountOnboarding
                    onExit={handleExit}
                  />
                </div>
              </ConnectComponentsProvider>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Powered by BookingFlows + Stripe</span>
            </div>
            <span>Secure • Encrypted • Verified</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeConnectOnboardingModal;
